import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import VideogameAssetOutlinedIcon from '@mui/icons-material/VideogameAssetOutlined';
import useHomeScreenApp from '../../utils/useHomeScreenApp';
import { ROUTES } from '../../constants';
import { brand } from '../../theme/colors';
import MediaListsHeader from './components/MediaListsHeader';

const TILES = [
  { label: 'Watchlist', route: ROUTES.watchlist, Icon: MovieOutlinedIcon, darkColor: brand.pink, lightColor: brand.pinkInk, accentRgb: '255,47,146' },
  { label: 'TV Watchlist', route: ROUTES.tvShows, Icon: TvOutlinedIcon, darkColor: brand.purple, lightColor: '#7c3aed', accentRgb: '139,92,246' },
  { label: 'Game List', route: ROUTES.gameList, Icon: SportsEsportsOutlinedIcon, darkColor: brand.blue, lightColor: '#0284c7', accentRgb: '56,189,248' },
  { label: 'Comic Shelf', route: ROUTES.comicShelf, Icon: AutoStoriesOutlinedIcon, darkColor: brand.pink, lightColor: brand.pinkInk, accentRgb: '255,47,146' },
  { label: 'Physical Movies', route: ROUTES.physicalMovieCollection, Icon: VideoLibraryOutlinedIcon, darkColor: brand.purple, lightColor: '#7c3aed', accentRgb: '139,92,246' },
  { label: 'Physical Games', route: ROUTES.physicalGameCollection, Icon: VideogameAssetOutlinedIcon, darkColor: brand.blue, lightColor: '#0284c7', accentRgb: '56,189,248' },
];

const MediaLists = () => {
  const navigate = useNavigate();
  useHomeScreenApp('/manifest-media-lists.json', 'Media Lists', '/icon-media-lists-192.png');

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isDesktop = window.innerWidth >= 600;
    if (!isStandalone && isDesktop) {
      navigate(ROUTES.hub, { replace: true });
    }
  }, [navigate]);

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <MediaListsHeader />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          maxWidth: 420,
          mx: 'auto',
          px: 2,
          pt: 3,
          pb: 4,
        }}
      >
        {TILES.map(({ label, route, Icon, darkColor, lightColor, accentRgb }) => (
          <Box
            key={label}
            component={Link}
            to={route}
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              aspectRatio: '1 / 1',
              borderRadius: 3,
              textDecoration: 'none',
              ...(theme.palette.mode === 'dark'
                ? {
                    background: `linear-gradient(140deg, rgba(${accentRgb},0.14), rgba(255,255,255,0.04))`,
                    border: '1px solid rgba(255,255,255,0.09)',
                    '&:hover': { background: `linear-gradient(140deg, rgba(${accentRgb},0.20), rgba(255,255,255,0.07))` },
                    '&:active': { background: `linear-gradient(140deg, rgba(${accentRgb},0.25), rgba(255,255,255,0.09))` },
                  }
                : {
                    background: `linear-gradient(140deg, rgba(${accentRgb},0.09), rgba(255,255,255,0.45))`,
                    border: `1px solid rgba(${accentRgb},0.25)`,
                    boxShadow: `0 1px 6px rgba(${accentRgb},0.18)`,
                    '&:hover': { background: `linear-gradient(140deg, rgba(${accentRgb},0.13), rgba(255,255,255,0.65))` },
                    '&:active': { background: `linear-gradient(140deg, rgba(${accentRgb},0.17), rgba(255,255,255,0.75))` },
                  }),
              transition: 'background 0.15s',
            })}
          >
            <Icon
              sx={(theme) => ({
                fontSize: 40,
                color: theme.palette.mode === 'dark' ? darkColor : lightColor,
              })}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                textAlign: 'center',
                lineHeight: 1.2,
                px: 1,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MediaLists;
